import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { StickyNote } from 'lucide-react';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { DocumentRichText } from '../../documents/form/inputs/DocumentRichText';
import {
    FolderNote,
    updateFolderNote,
} from '../../../services/api/folderService';
import { stripHtml } from '../descriptionUtils';

interface FolderNoteCardProps {
    folderId: string;
    note?: FolderNote;
    canManage: boolean;
    editing: boolean;
    onEditingChange: (editing: boolean) => void;
    onSaved: () => void;
}

const NOTE_TEXT_CLASSES =
    'text-sm leading-6 text-zinc-300 [&_h1]:mb-3 [&_h1]:mt-5 [&_h1]:text-xl [&_h1]:font-medium [&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:text-lg [&_h2]:font-medium [&_ol]:ml-5 [&_ol]:list-decimal [&_p:empty]:h-6 [&_ul]:ml-5 [&_ul]:list-disc';

const NoteEditor = ({
    folderId,
    note,
    onEditingChange,
    onSaved,
}: Omit<FolderNoteCardProps, 'canManage' | 'editing'>) => {
    const { t } = useTranslation();
    const [title, setTitle] = useState(note?.title ?? '');
    const [html, setHtml] = useState(note?.html ?? '');
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const save = async (next: FolderNote | null) => {
        setSaving(true);
        setError(null);
        try {
            await updateFolderNote(folderId, next);
            onEditingChange(false);
            onSaved();
        } catch {
            setError(t('folders.note.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    const handleSave = () => {
        if (!title.trim()) {
            setError(t('folders.note.headingRequired'));
            return;
        }
        if (!stripHtml(html)) {
            setError(t('folders.note.textRequired'));
            return;
        }
        save({ title: title.trim(), html });
    };

    return (
        <Card className="space-y-4 border-subtle p-4 shadow-none">
            <div className="space-y-2">
                <Label htmlFor="folder-note-heading">
                    {t('folders.note.headingLabel')}
                    <span className="ml-0.5 text-destructive">*</span>
                </Label>
                <Input
                    id="folder-note-heading"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t('folders.note.headingPlaceholder')}
                    maxLength={80}
                    autoFocus
                />
            </div>
            <DocumentRichText
                value={html}
                onChange={setHtml}
                disabled={saving}
                className="[&_.ProseMirror]:min-h-40"
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex flex-wrap items-center gap-2">
                {note && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => save(null)}
                        disabled={saving}
                    >
                        {t('folders.note.remove')}
                    </Button>
                )}
                <div className="ml-auto flex gap-2">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onEditingChange(false)}
                        disabled={saving}
                    >
                        {t('folders.create.cancel')}
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {t('folders.create.save')}
                    </Button>
                </div>
            </div>
        </Card>
    );
};

export const FolderNoteCard = (props: FolderNoteCardProps) => {
    const { note, canManage, editing, onEditingChange } = props;
    const { t } = useTranslation();
    const textRef = useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [overflows, setOverflows] = useState(false);
    const sanitizedHtml = useMemo(
        () => (note ? DOMPurify.sanitize(note.html) : ''),
        [note]
    );

    useLayoutEffect(() => {
        const el = textRef.current;
        if (!el || expanded) return;
        setOverflows(el.scrollHeight > el.clientHeight + 1);
    }, [sanitizedHtml, expanded]);

    if (editing) return <NoteEditor {...props} />;
    if (!note) return null;

    return (
        <Card className="overflow-hidden rounded-md border-subtle shadow-none">
            <header className="flex items-start justify-between gap-3 px-4 py-3">
                <div className="flex min-w-0 items-start gap-2.5">
                    <StickyNote
                        aria-hidden="true"
                        className="mt-0.5 h-4 w-4 shrink-0 text-subtle-foreground"
                    />
                    <h2 className="min-w-0 break-words text-sm font-semibold text-foreground">
                        {note.title}
                    </h2>
                </div>
                {canManage && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEditingChange(true)}
                        className="h-auto shrink-0 px-2 py-1 text-xs font-medium text-subtle-foreground hover:text-foreground"
                    >
                        {t('folders.note.edit')}
                    </Button>
                )}
            </header>
            <div className="pb-[18px] pl-[42px] pr-4">
                <div className="relative max-w-[66ch]">
                    <div
                        ref={textRef}
                        className={`${NOTE_TEXT_CLASSES} ${
                            expanded ? '' : 'max-h-[168px] overflow-hidden'
                        } [&>*:first-child]:mt-0 [&>*:last-child]:mb-0`}
                        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                    />
                    {overflows && !expanded && (
                        <div
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-b from-transparent to-card"
                        />
                    )}
                </div>
                {overflows && (
                    <button
                        type="button"
                        onClick={() => setExpanded((prev) => !prev)}
                        className="mt-2 text-xs font-medium text-subtle-foreground hover:text-foreground"
                    >
                        {expanded
                            ? t('folders.note.showLess')
                            : t('folders.note.readMore')}
                    </button>
                )}
            </div>
        </Card>
    );
};
